import { unsafeCSS, html } from "lit";
import { property, queryAssignedNodes } from "lit/decorators.js";
import { e } from "../../scoped-registry-mixin-2fqf-E5n.js";
import { ValidationController } from "../../utilities/controllers/validation.js";
import { requiredValidator } from "../../utilities/validators.js";
import { LeafElement } from "../leaf-element.js";
import { LeafCheckbox } from "../checkbox/checkbox.component.js";
import { LeafValidationMessage } from "../validation-message/validation-message.component.js";
const styles = '/*------------------------------------*\\\n    #RESET\n\\*------------------------------------*/\n/**\n * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/\n */\n*,\n::slotted(*),\n*:before,\n*:after {\n  box-sizing: border-box;\n}\n\n:host *,\n:host *::before,\n:host *::after {\n  box-sizing: inherit;\n}\n\n[hidden] {\n  display: none !important;\n}\n\n:host {\n  display: block;\n}\n:host .leaf-c-checkgroup__wrapper {\n  margin-left: auto;\n  width: calc(100% - var(--leaf-spacing-4));\n}\n:host ::slotted([slot=note]) {\n  display: block;\n  margin-top: var(--leaf-spacing-8);\n}\n\n:host([variant=rows]) ::slotted(leaf-checkbox:not(:first-child)) {\n  margin-top: var(--leaf-spacing-16);\n}\n\n:host([variant=columns]) slot:not([name]) {\n  align-items: center;\n  display: flex;\n}\n:host([variant=columns]) ::slotted(*:not(:first-child)),\n:host([variant=columns]) ::slotted(*:not(:last-child)) {\n  margin-right: var(--leaf-spacing-16);\n}\n\n:host([invalid]) ::slotted(leaf-checkbox) {\n  position: relative;\n}\n:host([invalid]) ::slotted(leaf-checkbox)::before {\n  border: var(--leaf-border-width-button-default) solid var(--leaf-color-border-error);\n  content: "";\n  border-radius: var(--leaf-border-radius-small);\n  height: var(--leaf-spacing-24);\n  position: absolute;\n  width: var(--leaf-spacing-24);\n}\n\n:host(:focus-visible) {\n  outline: var(--leaf-border-width-button-default) solid var(--leaf-color-border-active-outline);\n}\n\n:host([invalid]:focus-visible) {\n  outline: 0;\n  outline: var(--leaf-border-width-button-default) solid var(--leaf-color-border-error);\n}';
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let createUniqueId = 0;
const _LeafCheckgroup = class _LeafCheckgroup extends e(LeafElement) {
  constructor() {
    super(...arguments);
    this.internals = this.attachInternals();
    this.variant = "rows";
    this.validation = new ValidationController(this, {
      validators: [requiredValidator],
      nativeTarget: () => this
    });
  }
  get boxes() {
    var _a;
    return ((_a = this.defaultNodes) == null ? void 0 : _a.filter((node) => node instanceof LeafCheckbox)) || [];
  }
  get note() {
    return this.querySelector('[slot="note"]');
  }
  connectedCallback() {
    super.connectedCallback();
    this.validation.validate(this.value ? this.value : void 0);
    this.addEventListener("focusout", this.onFocusout);
  }
  validateBoxValues() {
    var _a;
    const value = (_a = this.boxes) == null ? void 0 : _a.map((box) => box.checked ? box.value : null).filter(Boolean).join(", ");
    this.value = value;
    this.validation.validate(this.value ? this.value : void 0);
    if (this.value) {
      this.setValueForm();
    }
  }
  async firstUpdated() {
    await this.updateComplete;
    const callback = () => {
      this.validateBoxValues();
      this.emit("leaf-checkgroup-change", { detail: { checkgroup: this } }, window);
    };
    const observer = new MutationObserver(callback);
    observer.observe(this, {
      attributeFilter: ["checked"],
      attributeOldValue: true,
      subtree: true
    });
    this.validateBoxValues();
    if (this.note) {
      this.id || (this.id = `leaf-checkgroup-${createUniqueId++}`);
      this.note.id = `${this.id}-note`;
      this.setAttribute("aria-describedby", this.note.id);
    }
    this.emit("leaf:element-rendered", {}, this);
  }
  async updated(changes) {
    await this.updateComplete;
    if (changes.has("invalid")) {
      this.boxes.forEach((box) => box.invalid = this.invalid);
    }
    if (changes.has("disabled")) {
      this.boxes.forEach((box) => box.disabled = this.disabled);
      this.validation.validate(this.value ? this.value : void 0);
    }
    if (changes.has("required")) {
      this.validation.validate(this.value ? this.value : void 0);
    }
    this.id || (this.id = `leaf-checkgroup-${createUniqueId++}`);
    this.emit("leaf:form-element-update");
  }
  render() {
    return html`
      <div class="leaf-c-checkgroup__wrapper" part="wrapper">
        <slot @slotchange=${this.onSlotchange}></slot>
        <slot name="note"></slot>
      </div>
      ${this.validation.render()}
    `;
  }
  onSlotchange() {
    if (typeof this.name === "string") {
      const name = this.name;
      this.boxes.forEach((box) => box.setAttribute("name", name));
    }
  }
  onFocusout(event) {
    if (!event.relatedTarget || !this.boxes.includes(event.relatedTarget)) {
      this.validation.validate(this.value);
    }
  }
  setValueForm() {
    const existingInput = this.internals.form.querySelector(`input[name="${this.name}"][id="${this.id}-hidden"]`);
    if (existingInput) {
      existingInput.remove();
    }
    if (this.name) {
      const newInput = document.createElement("input");
      newInput.name = this.name;
      newInput.id = `${this.id}-hidden`;
      newInput.hidden = true;
      newInput.value = this.value ? this.value : "";
      this.internals.form.appendChild(newInput);
    }
  }
};
_LeafCheckgroup.styles = unsafeCSS(styles);
_LeafCheckgroup.elementDefinitions = {
  "leaf-validation-message": LeafValidationMessage
};
_LeafCheckgroup.formAssociated = true;
let LeafCheckgroup = _LeafCheckgroup;
__decorateClass([
  property({ reflect: true })
], LeafCheckgroup.prototype, "variant", 2);
__decorateClass([
  queryAssignedNodes()
], LeafCheckgroup.prototype, "defaultNodes", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafCheckgroup.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafCheckgroup.prototype, "invalid", 2);
__decorateClass([
  property({ attribute: false })
], LeafCheckgroup.prototype, "validators", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafCheckgroup.prototype, "required", 2);
__decorateClass([
  property()
], LeafCheckgroup.prototype, "name", 2);
__decorateClass([
  property()
], LeafCheckgroup.prototype, "value", 2);
export {
  LeafCheckgroup
};
//# sourceMappingURL=checkgroup.component.js.map
